import { reactive, ref, computed, toRefs, Ref, ComputedRef, WritableComputedRef } from 'vue'
import { Store } from 'vuex'
import { date } from 'quasar'
import { Entry, EntryModel, RequestQueryItem, ServiceResponse } from '@Protium/vue-ozone-utils'

import { $get, $groupBy, $sortBy, SortingDirection } from '@/utils'
import { grep } from '@/utils/grep'

import { DATE_FORMAT } from '@/constants'
import { Selection } from '@Protium/vue-ozone-utils/types/models'

interface EntryListEvent<T = Entry> {
  items: Array<T>,
  contentType?: string,
  titleField?: string,
  callback?():any,
  groupBy?: any,
  target?: any
}

export interface ListGroup {
  [key: string]: Array<Entry>
}

export enum Mutation {
  create = 'Set',
  update = 'Set',
  remove = 'Remove',
  overwrite = 'Overwrite'
}

interface List<T = Entry>{
  // value(): Array<Entry>
  // _intermediate: Array<Entry>
  // intermediate: Array<Entry>
  // items: ComputedRef<Array<Entry> | Array<ListGroup>>
  applyFilters(value: Array<T>): Array<T>
  model: EntryModel | null
  loading: WritableComputedRef<boolean>
  set(item:T | Array<T>, action?:Mutation, prepend?: boolean): void
  _selection: Ref<Array<T> | null>
  selection: Ref<Array<T> | null>
  select(item:T | Array<T>): void
  _active: Ref<T | null>
  active: Ref<T | null>
  // tick(): void
  reset(): void
  // _group: string | Array<string> | ((item:T) => any)
  // groupBy: string | Array<string> | ((item:T) => any)
  _query: string
  _filterFn?(e:T, q:string): boolean,
  filterBy: string
  _sort: string | Array<string>
  sortBy: string | Array<string>
  _order: SortingDirection
  sortOrder: SortingDirection
}

interface Config {
  unique: boolean
}

type ContentValidator = (item:Entry) => void | Entry

const DEFAULT_CONFIG: Config = { unique: true }

// General functions for mutating orders etc.
export default function useContentModule<T extends Entry> (moduleName: string, $store: Store<any>, validator?: ContentValidator, config: Config = DEFAULT_CONFIG) {
  const type = $store.getters[`${moduleName}/Type`]

  const state = reactive<List<T>>({
    applyFilters (value) {
      return sortBy(this.sortBy, filterBy(this.filterBy, !config.unique ? value : uniqById(value)))
    },
    set (item, action = Mutation.update, prepend, partial = false) {
      // console.log('contentmodule.set', action, item)
      // NOTE store Set handles any parsing against the current data

      if (action === Mutation.remove) $store.dispatch(`${moduleName}/Remove`, item).catch(console.warn)
      else if (action === Mutation.overwrite) $store.dispatch(`${moduleName}/Replace`, item).catch(console.warn)
      else $store.dispatch(`${moduleName}/Set`, { data: item, options: { partial, prepend } }).catch(console.warn)
    },
    _selection: ref($store.getters[`${moduleName}/Selection`]),
    // These will act independantly of setting an active item,
    // but not that is multiselect is not set to true, when an item is made active,
    // the selection will reset to the active item. (the active item is otherwise appended to the selection)
    get selection () {
      return this._selection
    },
    set selection (item) {
      $store.dispatch(`${moduleName}/SetSelection`, item).catch(console.warn)
    },
    select (item) {
      $store.dispatch(`${moduleName}/Select`, item).catch(console.warn)
    },
    _active: ref($store.getters[`${moduleName}/ActiveItem`]),
    get active () {
      return this._active
    },
    set active (item) {
      $store.dispatch(`${moduleName}/Activate`, item).catch(console.warn)
    },
    model: ref($store.getters[`${moduleName}/Model`]) as unknown as EntryModel,
    loading: computed(() => $store.getters[`${moduleName}/Loading`]) as WritableComputedRef<boolean, boolean>,
    reset () {
      // this.groupBy = ''
      this.filterBy = ''
      this._filterFn = e => true
    },
    _query: '',
    _filterFn: e => true,
    get filterBy () {
      return this._query
    },
    set filterBy (query: string) {
      this._query = query || ''
    },
    _sort: 'sys.created_at',
    _order: SortingDirection.DESC,
    get sortOrder () {
      return this._order
    },
    set sortOrder (value) {
      this._order = value
    },
    get sortBy () {
      return this._sort
    },
    set sortBy (sort) {
      this._sort = sort || ''
    }
  })

  //  fix for getters / setters seemingly not working with toRefs
  const stateGetterSetterRefs = {
    loading: ref<boolean | undefined>(state.loading),
    active: ref<T | undefined | null>(state.active),
    model: ref<EntryModel | undefined>(state.model),
    selection: ref<Selection<T> | undefined>(state.selection),
    select: state.select,
    set: state.set,
    sortBy: state.sortBy,
    sortOrder: state.sortOrder
  }

  const items = computed<Array<T>>({
    get: () => state.applyFilters($store.getters[`${moduleName}/Items`] || []),
    set: (value) => state.set(value)
  })

  function clear () {
    state.set([], Mutation.overwrite)
  }

  function setMulti (value: boolean) {
    $store.dispatch(`${moduleName}/SetMulti`, value).catch(console.error)
  }

  function setFilterFn (fn:(e:T, q:string) => boolean) {
    state._filterFn = fn
  }

  function filterBy (query: string, payload: Array<T>) {
    if (query === null || query === undefined) return payload
    if (!payload) {
      state.filterBy = query
      return []
    }

    const trimmedQuery = query.trim()
    const fragments = trimmedQuery.split(',').filter(f => !!f).map(f => f.trim())
    // console.log('fragments',fragments)
    return payload
      .filter(state._filterFn === undefined
        ? f => true
        : fragments.length <= 1
          ? f => state._filterFn(f, trimmedQuery, trimmedQuery ? [trimmedQuery] : [])
            // && (!fragments.length ? true : fragments.some(s => !!grep.contents(f, s, 2)))
          : (f => state._filterFn(f, trimmedQuery, fragments)
            // && fragments.some(s => !!grep.contents(f, s, 2))
            )
      )

  }

  function uniqById (value) {
    return value.filter(f => !!f).filter((f, i, a) => a.findIndex(n => n.sys?.id === f.sys?.id) === i)
  }

  function sortBy (path: Array<string> | string, payload?: Array<T>): Array<T> {
    if (!path) return payload || []

    if (payload) {
      return $sortBy(payload, path, state.sortOrder)
    } else {
      state.sortBy = path
      return []
    }
  }

  // NOTE prefer to never use the tick argument to force an update, as it may have unwanted side-effects.
  async function fetch (query:Array<RequestQueryItem> = [], config?):Promise<Array<T>> {
    try {
      // console.log('fetch', config)

      const { items } = await $store.dispatch(`${moduleName}/Fetch`, { query, config }) as ServiceResponse<T>
      // if (tick) state.tick()

      return Promise.resolve(items || [])
    } catch (e:any) {
      console.error('Fetch error:', e)
      if (e.statusCode === 403) $store.dispatch('User/Logout').catch(console.error)
      return Promise.reject(e)
    }
  }

  async function fetchTimerange (timerange?: any, config?, select?:Array<string> | null, additionalQuery: Array<RequestQueryItem> = []) {
    try {
      let query: any[] = []

      if (timerange) {
        query = [
          { field: 'sys.created_at', method: 'gte', match: date.formatDate(timerange.start, DATE_FORMAT) },
          { field: 'sys.created_at', method: 'lte', match: date.formatDate(timerange.end, DATE_FORMAT) },
          ...(Array.isArray(select) ? [{ select }] : []),
          ...additionalQuery
        ]
      }

      const { items } = await $store.dispatch(`${moduleName}/Fetch`, { query, config }).catch(console.error)
      // NOTE ticking here will likely update the entry-list so fast the buffer will clear before it even resolved, resulting un duplicate entries
      // if (tick) state.tick()

      return items
    } catch (e) {
      console.error('Fetch error:', e)
    }
  }

  function mutate (item: T | EntryListEvent<T>, action?: Mutation, prepend = false) {
    // NOTE fix to support receiving raw entryList events,
    // since this is much easier to manage in templates without extra functions
    if ((item as EntryListEvent<T>).items) return state.set((item as EntryListEvent<T>).items, action, prepend)
    return state.set(item as T, action, prepend)
  }

  function setActive ($event) {
    // console.log('set', $event)
    if (!$event) return
    const entry = $event.items ? $event.items[0] : $event
    // console.log('>', entry)
    // console.log('<', items.value.find(item => entry.sys?.id === item.sys.id))

    // HOTFIX cast to any to fix vue UnwrapRef issue
    stateGetterSetterRefs.active.value = items.value.find(item => entry.sys?.id === item.sys.id) as any
  }

  return {
    ...toRefs(state),
    ...stateGetterSetterRefs,
    stateGetterSetterRefs,
    clear,
    setActive,
    items,
    contentType: type,
    fetch,
    fetchTimerange,
    mutate,
    requestHandler: $store.getters['Services/Directr'],
    assert: {
      active: (item: T): boolean => !!item && !!stateGetterSetterRefs.active.value && item.sys.id === stateGetterSetterRefs.active.value.sys.id
    },
    setMulti,
    // NOTE these naturally overwrite the state values by the same name, acting as a front
    setFilterFn,
    filterBy,
    sortBy,
    uniqById
  }
}
